import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
	faArrowRight,
	faFileMagnifyingGlass,
	faScaleBalanced,
	faNetworkWired,
} from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { ROUTE_CONFIG } from "@/routes/config";

const CARD_ICONS = [faFileMagnifyingGlass, faScaleBalanced, faNetworkWired];

type Feature = {
	title: string;
	description: string;
	module: string;
	solution: string;
};

export default function LandingThreeColumns() {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const ts = (key: string) => t(`questionnaire.landingThreeColumns.${key}`);

	const features = t("questionnaire.landingThreeColumns.features", {
		returnObjects: true,
	}) as Feature[];

	return (
		<section className="relative w-full px-4 md:px-0 overflow-hidden">
			{/* soft decorative glow */}
			<div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(70,128,252,0.08),transparent_65%)]" />

			<div className="relative w-full max-w-[1420px] mx-auto my-16 sm:my-[7.5rem] flex flex-col items-center gap-14 px-4 md:px-16">
				<div className="flex flex-col items-center gap-4 text-center max-w-[760px]">
					<span className="font-urbanist text-[13px] font-bold uppercase tracking-[0.12em] text-[#4680FC] bg-[#F0F4FF] border border-[#D8E4FF] rounded-full px-4 py-1.5">
						{ts("kicker")}
					</span>
					<h2 className="font-urbanist text-[36px] md:text-[40px] font-semibold leading-[120%] text-[#1D243C]">
						{ts("title")}
					</h2>
					<p className="font-urbanist text-[18px] font-normal leading-[150%] text-[#626373]">
						{ts("subtitle")}
					</p>
				</div>

				<div className="w-full grid grid-cols-1 md:grid-cols-3 gap-6">
					{features?.map((feature, index) => (
						<div
							key={feature.title}
							className="group relative flex flex-col gap-5 rounded-2xl border border-[#E6E6EC] bg-white p-7 shadow-[0_2px_10px_rgba(13,23,55,0.04)] transition-all duration-300 hover:-translate-y-1.5 hover:border-[#4680FC]/40 hover:shadow-[0_24px_48px_rgba(13,23,55,0.12)]"
						>
							<div className="flex items-center gap-4">
								<div className="flex w-12 h-12 shrink-0 rounded-xl bg-[linear-gradient(135deg,#4680FC,#1A2E6E)] justify-center items-center shadow-[0_8px_16px_rgba(70,128,252,0.3)] transition-transform duration-300 group-hover:scale-110">
									<Icon className="w-5 h-5 text-white" icon={CARD_ICONS[index]} />
								</div>
								<h3 className="font-urbanist text-[20px] font-bold text-[#1D243C] leading-[130%]">
									{feature.title}
								</h3>
							</div>

							<div className="flex flex-col gap-2">
								<div className="flex items-center gap-2">
									<span className="w-1.5 h-1.5 rounded-full bg-[#FF6B5E]" />
									<span className="font-urbanist text-[12px] font-bold uppercase tracking-[0.1em] text-[#626373]">
										{ts("problemLabel")}
									</span>
								</div>
								<p className="font-urbanist text-[15px] leading-[160%] text-[#626373]">
									{feature.description}
								</p>
							</div>

							<div className="mt-auto flex flex-col gap-3 rounded-xl bg-[#F4F8FF] border border-[#E0EAFF] p-5 transition-colors duration-300 group-hover:bg-[#EDF3FF]">
								<div className="flex items-center gap-2">
									<span className="w-1.5 h-1.5 rounded-full bg-[#2AA216]" />
									<span className="font-urbanist text-[12px] font-bold uppercase tracking-[0.1em] text-[#2F62D9]">
										{ts("solutionLabel")}
									</span>
								</div>
								<p className="font-urbanist text-[15px] leading-[160%] text-[#1D243C]">
									{feature.solution}
								</p>
								<button
									className="flex items-center gap-2 w-fit font-urbanist text-[13px] font-semibold text-[#2F62D9] hover:text-[#1A2E6E] transition-colors"
									type="button"
									onClick={() => navigate(ROUTE_CONFIG.PRODUCT)}
								>
									<span className="bg-white border border-[#D8E4FF] rounded-full px-3 py-1">
										{feature.module}
									</span>
									<Icon
										className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1"
										icon={faArrowRight}
									/>
								</button>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
